import React from "react";
import { typography, surfaces, borders, textColors, interactive } from "../../../design-system/tokens";
import useBalancedColumns from "../../hooks/useBalancedColumns";

// Map each choice to a Tailwind rounded class for the preview
const ROUNDED_CLASS_MAP = {
  "rounded-none": "rounded-none",
  "rounded-sm": "rounded-sm",
  "rounded-md": "rounded-md",
  "rounded-lg": "rounded-lg",
  "rounded-xl": "rounded-xl",
  "rounded-2xl": "rounded-2xl",
  "rounded-3xl": "rounded-3xl",
  "rounded-full": "rounded-full",
};

const DEFAULT_CHOICES = [
  { value: "rounded-none", label: "None" },
  { value: "rounded-sm", label: "Small" },
  { value: "rounded-md", label: "Medium" },
  { value: "rounded-lg", label: "Large" },
  { value: "rounded-xl", label: "X Large" },
  { value: "rounded-2xl", label: "2X Large" },
  { value: "rounded-3xl", label: "3X Large" },
  { value: "rounded-full", label: "Full" },
];

export default function RoundedBorderField({ label, help, value, onChange, choices, infoText }) {
  const opts = Array.isArray(choices) && choices.length
    ? choices.map((c) => ({ value: c.value, label: c.label }))
    : DEFAULT_CHOICES;

  const normalized = (value || "rounded-none");

  const [gridRef, columns] = useBalancedColumns(opts.length, { minItemWidth: 85, gap: 8 });

  return (
    <div className="field-wrapper">
      {label && (
        <label className={`block mb-2 ${typography.label} ${textColors.secondary}`}>
          {label}
        </label>
      )}

      <div ref={gridRef} className="grid gap-2" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
        {opts.map((opt) => {
          const selected = normalized === opt.value;
          const roundedClass = ROUNDED_CLASS_MAP[opt.value] || "rounded-none";
          const previewBorderColor = selected
            ? "border-white/70"
            : "border-gray-400 dark:border-gray-500";

          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange(opt.value)}
              className={`flex flex-col items-center p-3 border rounded-lg transition-all ${
                selected 
                  ? `${interactive.selectedBg} text-white ${interactive.selectedBorder} shadow-sm`
                  : `${borders.default} ${surfaces.primary} ${textColors.primary} ${surfaces.hover}`
              }`}
              aria-pressed={selected}
              title={opt.label}
            >
              {/* Show a full rectangle with rounded corners */}
              <div className="w-full h-12 flex items-center justify-center mb-2">
                <div
                  className={`w-16 h-10 border-[3px] ${roundedClass} ${previewBorderColor}`}
                />
              </div>
              <span className={`${typography.caption} ${selected ? "text-white/90" : textColors.primary}`}>
                {opt.label}
              </span>
            </button>
          );
        })}
      </div>

      {help && (
        <div className={`mt-2 ${typography.caption} ${textColors.tertiary}`}>
          {help}
        </div>
      )}
    </div>
  );
}
